import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { motion, useReducedMotion } from "framer-motion";
import { toast } from "sonner";
import { api } from "../../api";
import { MIGRATION } from "../../constants/testIds";
import { fadeUp } from "../../lib/motion";
import { buildMigrationRow } from "../../lib/migrationParse";
import { GymLoader } from "../../components/common/GymLoader";
import { UploadStep } from "../../components/migration/steps/UploadStep";
import { MapColumnsStep } from "../../components/migration/steps/MapColumnsStep";
import { DedupeStep } from "../../components/migration/steps/DedupeStep";
import { MidCycleStep } from "../../components/migration/steps/MidCycleStep";
import { PreviewStep } from "../../components/migration/steps/PreviewStep";
import { CommitStep } from "../../components/migration/steps/CommitStep";

const STEP_LABELS = ["Upload", "Map columns", "Dedupe & clean", "Mid-cycle state", "Preview", "Commit"];

// UX-P04 SCR-P04-10..15 - the full 6-step Migration Studio wizard. Holds every bit of client-side
// state (uploaded rows never leave the browser until Preview); persists Steps 1-4 progress as a
// server-side `draft` after every step so closing the browser never loses work.
export default function MigrationWizard() {
  const reducedMotion = useReducedMotion();
  const navigate = useNavigate();
  const { batchId: routeBatchId } = useParams();
  const isNew = routeBatchId === "new";

  const [step, setStep] = useState(1);
  const [batchId, setBatchId] = useState(isNew ? null : routeBatchId);
  const [files, setFiles] = useState([]); // [{ name, headers, rows, error }]
  const [mapping, setMapping] = useState({}); // { header: field|null }
  const [dedupeDecisions, setDedupeDecisions] = useState({}); // { phoneKey: { action, primaryRowKey } }
  const [fieldOverrides, setFieldOverrides] = useState({}); // { row_key: { field: value } } - bulk-fill + inline fixes
  const [previewResult, setPreviewResult] = useState(null);
  const [resumedFromServer, setResumedFromServer] = useState(false);

  const { data: existingBatch, isLoading: loadingBatch } = useQuery({
    queryKey: ["migration-batch", routeBatchId],
    queryFn: () => api.get(`/migration/${routeBatchId}`).then((r) => r.data),
    enabled: !isNew,
  });

  useEffect(() => {
    if (!existingBatch || resumedFromServer) return;
    setResumedFromServer(true);
    setBatchId(existingBatch.id);
    setMapping(existingBatch.mapping || {});
    setDedupeDecisions(existingBatch.dedupe_decisions || {});
    if (existingBatch.status === "draft") {
      setStep(existingBatch.wizard_step || 1);
      if (existingBatch.wizard_step >= 2) {
        toast.info("Resuming a draft - please re-upload the same file(s) so your saved mapping/dedupe choices can be reapplied.");
      }
    } else {
      // previewed/committing/committed/rolled_back all already have full row data server-side.
      setPreviewResult(existingBatch);
      setStep(existingBatch.status === "previewed" ? 5 : 6);
    }
  }, [existingBatch, resumedFromServer]);

  const draftMutation = useMutation({
    mutationFn: (payload) => api.post("/migration/draft", { batch_id: batchId, ...payload }),
    onSuccess: (r) => setBatchId(r.data.id),
  });

  const saveDraftAndGo = (nextStep, extra = {}) => {
    draftMutation.mutate({
      wizard_step: nextStep,
      source_files: files.map((f) => ({ name: f.name, row_count: f.rows.length })),
      mapping, dedupe_decisions: dedupeDecisions,
      ...extra,
    });
    setStep(nextStep);
  };

  // Every raw row, mapped through the current column mapping (before dedupe grouping/overrides).
  const mappedRows = useMemo(() => {
    const out = [];
    for (const f of files) {
      f.rows.forEach((raw, idx) => out.push(buildMigrationRow(raw, mapping, f.name, idx)));
    }
    return out;
  }, [files, mapping]);

  if (!isNew && loadingBatch) return <GymLoader />;

  return (
    <div className="space-y-5 max-w-4xl" data-testid={MIGRATION.wizardPage}>
      <motion.div {...fadeUp(reducedMotion)}>
        <h1 className="text-2xl font-bold" style={{ color: "var(--scarlet-text)", letterSpacing: "-0.02em" }}>Migration wizard</h1>
        <div className="flex items-center gap-1.5 mt-3 flex-wrap">
          {STEP_LABELS.map((label, i) => (
            <span
              key={label} data-testid={MIGRATION.wizardStepLabel(i + 1)}
              className="text-xs font-medium rounded-full px-3 py-1"
              style={step === i + 1
                ? { background: "var(--scarlet-accent)", color: "#fff" }
                : step > i + 1
                  ? { background: "var(--scarlet-surface-2)", color: "var(--scarlet-text)" }
                  : { background: "transparent", border: "1px solid var(--scarlet-hairline)", color: "var(--scarlet-text-secondary)" }}
            >
              {i + 1}. {label}
            </span>
          ))}
        </div>
      </motion.div>

      {step === 1 && (
        <UploadStep
          files={files} setFiles={setFiles}
          onContinue={() => saveDraftAndGo(2)}
        />
      )}
      {step === 2 && (
        <MapColumnsStep
          files={files} mapping={mapping} setMapping={setMapping}
          onBack={() => setStep(1)} onContinue={() => saveDraftAndGo(3)}
        />
      )}
      {step === 3 && (
        <DedupeStep
          mappedRows={mappedRows} dedupeDecisions={dedupeDecisions} setDedupeDecisions={setDedupeDecisions}
          fieldOverrides={fieldOverrides} setFieldOverrides={setFieldOverrides}
          onBack={() => setStep(2)} onContinue={() => saveDraftAndGo(4)}
        />
      )}
      {step === 4 && (
        <MidCycleStep mapping={mapping} onBack={() => setStep(3)} onContinue={() => saveDraftAndGo(5)} />
      )}
      {step === 5 && (
        <PreviewStep
          batchId={batchId} setBatchId={setBatchId}
          files={files} mapping={mapping} mappedRows={mappedRows}
          dedupeDecisions={dedupeDecisions} fieldOverrides={fieldOverrides} setFieldOverrides={setFieldOverrides}
          previewResult={previewResult} setPreviewResult={setPreviewResult}
          onBack={() => setStep(4)} onContinue={() => setStep(6)}
        />
      )}
      {step === 6 && (
        <CommitStep batchId={batchId} onDone={() => navigate("/app/migration")} />
      )}
    </div>
  );
}
